export const ASSIGNMENT_REQUEST_COOLDOWN_MS = 10_000;

export const ASSIGNMENT_REQUEST_COOLDOWN_STORAGE_KEY =
  'assignmentRequestCooldownUntil';

export const ASSIGNMENT_REQUEST_LOCK_NAME = 'assignmentRequest';

let inMemoryCooldownUntil = 0;
let isRequestInFlight = false;

const readStoredCooldownUntil = (): number => {
  try {
    const stored = Number(
      window.localStorage.getItem(ASSIGNMENT_REQUEST_COOLDOWN_STORAGE_KEY),
    );
    return Number.isFinite(stored) ? stored : 0;
  } catch {
    return 0;
  }
};

const writeStoredCooldownUntil = (until: number): boolean => {
  try {
    window.localStorage.setItem(
      ASSIGNMENT_REQUEST_COOLDOWN_STORAGE_KEY,
      String(until),
    );
    return true;
  } catch {
    return false;
  }
};

export const getAssignmentRequestCooldownRemainingMs = (
  now: number = Date.now(),
): number => {
  const latestValidUntil = now + ASSIGNMENT_REQUEST_COOLDOWN_MS;
  const cooldownUntil = Math.max(
    ...[readStoredCooldownUntil(), inMemoryCooldownUntil].filter(
      (until) => until <= latestValidUntil,
    ),
    0,
  );
  return Math.max(cooldownUntil - now, 0);
};

export const startAssignmentRequestCooldown = (
  now: number = Date.now(),
): void => {
  const until = now + ASSIGNMENT_REQUEST_COOLDOWN_MS;
  inMemoryCooldownUntil = until;
  writeStoredCooldownUntil(until);
};

export type ExclusiveResult<T> = { ran: true; result: T } | { ran: false };

export const runAssignmentRequestExclusively = async <T>(
  fn: () => Promise<T>,
): Promise<ExclusiveResult<T>> => {
  if (isRequestInFlight) return { ran: false };
  isRequestInFlight = true;
  try {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
    if (!locks?.request) {
      return { ran: true, result: await fn() };
    }
    return await locks.request(
      ASSIGNMENT_REQUEST_LOCK_NAME,
      { ifAvailable: true },
      async (lock): Promise<ExclusiveResult<T>> =>
        lock ? { ran: true, result: await fn() } : { ran: false },
    );
  } finally {
    isRequestInFlight = false;
  }
};

export const resetAssignmentRequestThrottleForTests = (): void => {
  inMemoryCooldownUntil = 0;
  isRequestInFlight = false;
};
